import { componentDisplayName, type ComponentKind, type DeviceComponent } from './Component';
import type { StationThresholds } from './Thresholds';

export type AlertDirection = 'below' | 'above';

// Lo que mide el propio dispositivo (incluye derivadas de la estación:
// lluvia acumulada y Delta T). Ver backend/src/domain/entities/Alert.ts.
export type SensorAlertMetric =
  | 'temperature'
  | 'relative_humidity'
  | 'wind_speed'
  | 'soil_moisture'
  | 'rain_1h'
  | 'rain_24h'
  | 'delta_t'
  | 'wet_hours_24h';
// Clima de la ubicación del dispositivo (Open-Meteo).
export type WeatherMetric = 'temperature' | 'precipitation_probability' | 'wind_speed';

export type AlertCondition =
  // channel: solo soil_moisture — qué sonda de la estación.
  | {
      source: 'sensor';
      metric: SensorAlertMetric;
      channel?: number | null;
      direction: AlertDirection;
      threshold: number;
    }
  | {
      source: 'weather';
      metric: WeatherMetric;
      direction: AlertDirection;
      threshold: number;
    }
  // El lote de la estación está en alguna de estas etapas (por nombre).
  | {
      source: 'lot';
      metric: 'stage';
      stages: string[];
    };

export interface Alert {
  id: string;
  deviceId: string;
  deviceName: string;
  name: string | null;
  // Todas tienen que cumplirse a la vez (Y).
  conditions: AlertCondition[];
  enabled: boolean;
  triggered: boolean;
  triggeredAt: string | null;
}

export const MAX_ALERT_CONDITIONS = 6;

// Qué se puede vigilar según lo que tiene enchufado la estación.
const ALERT_METRICS_BY_COMPONENT: Record<ComponentKind, SensorAlertMetric[]> = {
  air: ['temperature', 'relative_humidity', 'delta_t', 'wet_hours_24h'],
  // Todavía sin alertas de presión (la útil sería "bajó X hPa en 3 h").
  pressure: [],
  rain: ['rain_1h', 'rain_24h'],
  wind: ['wind_speed'],
  soil: ['soil_moisture'],
  valve: [],
};

export interface AlertableSensor {
  metric: SensorAlertMetric;
  channel: number | null;
  label: string;
}

// Una opción por métrica (y por sonda en humedad de suelo).
export function alertableSensors(components: DeviceComponent[]): AlertableSensor[] {
  const soilCount = components.filter((c) => c.enabled && c.kind === 'soil').length;
  return components
    .filter((c) => c.enabled)
    .sort((a, b) => a.channel - b.channel)
    .flatMap((c) =>
      ALERT_METRICS_BY_COMPONENT[c.kind].map((metric) => ({
        metric,
        channel: c.kind === 'soil' ? c.channel : null,
        label:
          c.kind === 'soil' && soilCount > 1
            ? `${SENSOR_ALERT_METRIC_INFO[metric].label} · ${componentDisplayName(c)}`
            : SENSOR_ALERT_METRIC_INFO[metric].label,
      }))
    );
}

export const WEATHER_METRICS: WeatherMetric[] = ['temperature', 'precipitation_probability', 'wind_speed'];

interface MetricInfo {
  label: string;
  unit: string;
  hint?: string;
}

export const SENSOR_ALERT_METRIC_INFO: Record<SensorAlertMetric, MetricInfo> = {
  temperature: { label: 'Temperatura', unit: '°C' },
  relative_humidity: { label: 'Humedad relativa', unit: '%' },
  wind_speed: { label: 'Viento', unit: 'km/h' },
  soil_moisture: { label: 'Humedad de suelo', unit: '%' },
  rain_1h: { label: 'Lluvia en la última hora', unit: 'mm' },
  rain_24h: { label: 'Lluvia en 24 h', unit: 'mm' },
  delta_t: {
    label: 'Delta T',
    unit: '',
    hint: 'Entre 2 y 8 es la ventana ideal para pulverizar.',
  },
  wet_hours_24h: {
    label: 'Horas de hoja mojada (24 h)',
    unit: 'h',
    hint: 'Horas con la temperatura a 2 °C o menos del punto de rocío. Muchas favorecen royas, fusariosis y manchas.',
  },
};

export const LOT_STAGE_INFO: MetricInfo = {
  label: 'Etapa del lote',
  unit: '',
  hint: 'Se cumple mientras el lote de la estación esté en alguna de las etapas elegidas.',
};

export const WEATHER_METRIC_INFO: Record<WeatherMetric, MetricInfo> = {
  temperature: { label: 'Temperatura (clima de la zona)', unit: '°C' },
  precipitation_probability: { label: 'Probabilidad de lluvia', unit: '%' },
  wind_speed: { label: 'Viento (clima de la zona)', unit: 'km/h' },
};

export const directionLabel: Record<AlertDirection, string> = {
  below: 'Por debajo de',
  above: 'Por encima de',
};

export function supportsAlerts(components: DeviceComponent[]): boolean {
  return alertableSensors(components).length > 0;
}

export function conditionInfo(condition: Pick<AlertCondition, 'source' | 'metric'>): MetricInfo {
  if (condition.source === 'lot') return LOT_STAGE_INFO;
  return condition.source === 'sensor'
    ? SENSOR_ALERT_METRIC_INFO[condition.metric as SensorAlertMetric]
    : WEATHER_METRIC_INFO[condition.metric as WeatherMetric];
}

const formatNumber = (n: number) => String(Math.round(n * 10) / 10).replace('.', ',');

// "Viento > 20 km/h", o con el valor medido: "Viento 25 km/h (> 20)".
export function describeCondition(condition: AlertCondition, value?: number | null): string {
  if (condition.source === 'lot') return `Lote en ${condition.stages.join(' o ')}`;
  const info = conditionInfo(condition);
  const label =
    condition.source === 'sensor' && condition.channel ? `${info.label} (sonda ${condition.channel})` : info.label;
  const unit = info.unit ? ` ${info.unit}` : '';
  const op = condition.direction === 'below' ? '<' : '>';
  if (value === undefined || value === null) return `${label} ${op} ${formatNumber(condition.threshold)}${unit}`;
  return `${label} ${formatNumber(value)}${unit} (${op} ${formatNumber(condition.threshold)})`;
}

export function describeConditions(conditions: AlertCondition[]): string {
  return conditions.map((c) => describeCondition(c)).join(' y ');
}

// Atajos para armar alertas típicas de una estación en un toque, con los
// criterios de esa estación (helada y ventana de pulverización).
export function stationAlertTemplates(
  t: StationThresholds
): { label: string; name: string; conditions: AlertCondition[] }[] {
  return [
    {
      label: 'Helada',
      name: 'Riesgo de helada',
      conditions: [
        {
          source: 'sensor',
          metric: 'temperature',
          direction: 'below',
          threshold: t.frost.riskC,
        },
      ],
    },
    {
      label: 'Calor + viento',
      name: 'Calor con viento',
      conditions: [
        {
          source: 'sensor',
          metric: 'temperature',
          direction: 'above',
          threshold: 40,
        },
        {
          source: 'sensor',
          metric: 'wind_speed',
          direction: 'above',
          threshold: 20,
        },
      ],
    },
    {
      label: 'Lluvia fuerte',
      name: 'Lluvia fuerte',
      conditions: [
        {
          source: 'sensor',
          metric: 'rain_24h',
          direction: 'above',
          threshold: 30,
        },
      ],
    },
    {
      label: 'Pulverizar',
      name: 'Ventana de pulverización',
      conditions: [
        {
          source: 'sensor',
          metric: 'delta_t',
          direction: 'above',
          threshold: t.spray.deltaTMin,
        },
        {
          source: 'sensor',
          metric: 'delta_t',
          direction: 'below',
          threshold: t.spray.deltaTMax,
        },
        {
          source: 'sensor',
          metric: 'wind_speed',
          direction: 'above',
          threshold: t.spray.windMin,
        },
        {
          source: 'sensor',
          metric: 'wind_speed',
          direction: 'below',
          threshold: t.spray.windMax,
        },
      ],
    },
    {
      label: 'Suelo seco',
      name: 'Suelo seco',
      conditions: [
        {
          source: 'sensor',
          metric: 'soil_moisture',
          direction: 'below',
          threshold: t.soil.dryPct,
        },
      ],
    },
  ];
}

// Un atajo sirve si la estación mide todo lo que usa (ej. "Pulverizar" pide clima y viento).
export function templateFits(template: { conditions: AlertCondition[] }, components: DeviceComponent[]): boolean {
  const available = alertableSensors(components).map((a) => a.metric);
  return template.conditions.every((c) => c.source !== 'sensor' || available.includes(c.metric));
}

// Clave de una condición en el formulario: "source:metric" o, para una
// sonda de suelo, "sensor:soil_moisture:2" — así un solo select elige
// entre lo que mide la estación (por sonda), el clima de la zona y la etapa
// del lote ("lot:stage").
export function conditionKey(c: Pick<AlertCondition, 'source' | 'metric'> & { channel?: number | null }): string {
  return c.source === 'sensor' && c.channel ? `${c.source}:${c.metric}:${c.channel}` : `${c.source}:${c.metric}`;
}

export function parseConditionKey(key: string): {
  source: 'sensor' | 'weather' | 'lot';
  metric: string;
  channel: number | null;
} {
  const [source, metric, channel] = key.split(':');
  return {
    source: source as 'sensor' | 'weather' | 'lot',
    metric,
    channel: channel ? Number(channel) : null,
  };
}

// Tono e ícono de una alerta en la lista: el de su primera condición.
export type AlertTone = 'temp' | 'humidity' | 'wind' | 'rain' | 'soil' | 'stage' | 'spray';

export function alertTone(condition: AlertCondition | undefined): AlertTone {
  if (!condition) return 'temp';
  switch (condition.metric) {
    case 'relative_humidity':
    case 'wet_hours_24h':
      return 'humidity';
    case 'wind_speed':
      return 'wind';
    case 'rain_1h':
    case 'rain_24h':
    case 'precipitation_probability':
      return 'rain';
    case 'soil_moisture':
      return 'soil';
    case 'stage':
      return 'stage';
    case 'delta_t':
      return 'spray';
    default:
      return 'temp';
  }
}
